import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import * as R from 'ramda';

import textConstants from '@atom/constants/textConstants';
import {
  CREATE_DASHBOARD_CARD,
  DASHBOARD_CARDS_LAYOUT_UPDATE,
  DELETE_DASHBOARD_CARD,
  GET_DASHBOARD_CARDS,
  GET_DASHBOARD_CARDS_LAYOUT,
  UPDATE_DASHBOARD_CARD,
} from '@atom/graph/dashboard';
import { useUserProfile } from '@atom/hooks/useUserProfile';
import { Progress, Snackbar } from '@atom/mui';
import colors from '@atom/styles/colors';
import {
  ColumnsData,
  DashboardCard,
  DashboardCardContentState,
  DashboardCardCreateInput,
  DashboardCardSize,
  DashboardCardType,
} from '@atom/types/dashboard';
import { updateAtId } from '@atom/utilities/stateUtilities';

import CustomizeDashboardModal from './CustomizeDashboardModal/CustomizeDashboardModal';
import DashboardContext from './DashboardContext';
import DashboardEmptyState from './DashboardEmptyState';
import DashboardGrid from './DashboardGrid';
import { CARD_CONFIG } from './dashboardSettings';
import DashboardTitleBar from './DashboardTitleBar';
import {
  getColumn,
  getColumnIndex,
  getColumnsData,
  initCards,
  moveCardBetweenColumns,
} from './dashboardUtilities';

import classes from './dashboard.module.css';

const DashboardPortal = () => {
  const { userId, firstName } = useUserProfile();
  const [allCards, setAllCards] = useState<DashboardCard[]>([]);
  const [loadingInitData, setLoadingInitData] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [showCustomize, setShowCustomize] = useState<boolean>(false);
  const [layoutId, setLayoutId] = useState<string>();

  const [getDashboard] = useLazyQuery(GET_DASHBOARD_CARDS, {
    fetchPolicy: 'network-only',
  });

  const [getDashboardLayout] = useLazyQuery(GET_DASHBOARD_CARDS_LAYOUT, {
    fetchPolicy: 'network-only',
  });

  const getDashboardRequest = useCallback(async () => {
    const { data } = await getDashboard({
      variables: {
        input: { userId },
      },
    });

    const cards = R.pathOr([], ['dashboardCards'], data);

    if (R.isEmpty(cards)) {
      setLoadingInitData(false);
      return;
    }

    const { data: layoutData } = await getDashboardLayout({
      variables: {
        input: { userId },
      },
    });

    const columns = R.pathOr(
      [],
      ['dashboardCardsLayout', 'columns'],
      layoutData,
    );
    setLayoutId(R.pathOr(null, ['dashboardCardsLayout', 'id'], layoutData));
    setAllCards(initCards(cards, columns));
    setLoadingInitData(false);
  }, []);

  useEffect(() => {
    getDashboardRequest();
  }, []);

  const [callUpdateLayout, { loading: updatingLayout }] = useMutation(
    DASHBOARD_CARDS_LAYOUT_UPDATE,
  );

  const [createDashboardCard, { loading: creatingCard }] = useMutation(
    CREATE_DASHBOARD_CARD,
  );

  const [deleteDashboardCard, { loading: deletingCard }] = useMutation(
    DELETE_DASHBOARD_CARD,
  );

  const [updateDashboardCard, { loading: updatingCard }] = useMutation(
    UPDATE_DASHBOARD_CARD,
  );

  const updateLayout = (columns: ColumnsData): Promise<any> =>
    callUpdateLayout({
      variables: { input: { id: layoutId, userId, columns } },
    });

  const updateCardState = (
    card: DashboardCard,
    state: DashboardCardContentState,
  ) => {
    const cardUpdatedIdx = allCards.indexOf(card);
    setAllCards(prevAllCards =>
      R.update(cardUpdatedIdx, { ...card, contentState: state }, prevAllCards),
    );
  };

  const updateAllCardsState = (state: DashboardCardContentState) => {
    // Good example of when prevState is needed in useState hook
    //  - setting contentState by mapping over allCards
    //    carries over stale values and causes LOADING to persist on cards
    setAllCards(prevAllCards =>
      prevAllCards.map(card => ({
        ...card,
        contentState: state,
      })),
    );
  };

  const enabledCards: DashboardCard[] = allCards.filter(
    card => card.order !== -1,
  );

  const lgColumn: DashboardCard[] = useMemo(
    () => getColumn(DashboardCardSize.LG, enabledCards),
    [enabledCards],
  );
  const smColumn: DashboardCard[] = useMemo(
    () => getColumn(DashboardCardSize.SM, enabledCards),
    [enabledCards],
  );
  const someCardIsLoading: boolean = useMemo(
    () => enabledCards.some(card => card.contentState === 'LOADING'),
    [enabledCards],
  );

  const emptyBoard = R.isEmpty(enabledCards);

  const displayError = () => {
    setLoading(false);
    Snackbar.error({
      message: textConstants.GENERIC_APPLICATION_ERROR_TEXT,
    });
  };

  const handleAddEmbedCard = async (card: DashboardCard) => {
    setLoading(true);
    const targetColumnIdx = getColumnIndex(card.size);
    const columns: ColumnsData = getColumnsData(lgColumn, smColumn);
    const order = columns[targetColumnIdx].length;
    const input: DashboardCardCreateInput = {
      userId,
      type: DashboardCardType.EMBED,
      title: card.title,
      size: card.size,
      embedLink: card.embedLink,
    };

    let newCard: DashboardCard;
    try {
      const newCardData = await createDashboardCard({ variables: { input } });
      newCard = R.path(['data', 'dashboardCardCreate'], newCardData);
      columns[targetColumnIdx] = [...columns[targetColumnIdx], newCard.id];
      await updateLayout(columns);
      setShowCustomize(false);
      setAllCards(prevAllCards => [
        ...prevAllCards,
        {
          ...newCard,
          order,
          contentState: DashboardCardContentState.LOADING,
          ...CARD_CONFIG[DashboardCardType.EMBED],
        },
      ]);
      setLoading(false);
    } catch (error) {
      displayError();
    }
  };

  const handleDeleteEmbedCard = async (card: DashboardCard) => {
    setLoading(true);
    const targetColumnIdx = getColumnIndex(card.size);
    const columns: ColumnsData = getColumnsData(lgColumn, smColumn);

    try {
      await deleteDashboardCard({
        variables: { input: { userId, id: card.id } },
      });
      columns[targetColumnIdx] = R.without([card.id], columns[targetColumnIdx]);
      await updateLayout(columns);
      setAllCards(prevAllCards => R.without([card], prevAllCards));
      setLoading(false);
    } catch (error) {
      displayError();
    }
  };

  const handleEditEmbedCard = async (newCardFields: DashboardCard) => {
    setLoading(true);
    const cardId = newCardFields.id;
    const cardToUpdate: DashboardCard = allCards.find(
      card => card.id === cardId,
    );
    const columnSwap: boolean = cardToUpdate.size !== newCardFields.size;
    const nameChangeOnly: boolean =
      cardToUpdate.size === newCardFields.size &&
      cardToUpdate.embedLink === newCardFields.embedLink &&
      cardToUpdate.title !== newCardFields.title;
    let newOrder: number = cardToUpdate.order;

    try {
      await updateDashboardCard({
        variables: {
          input: {
            userId,
            id: cardId,
            title: newCardFields.title,
            size: newCardFields.size,
            embedLink: newCardFields.embedLink,
          },
        },
      });

      if (columnSwap) {
        const targetColumnIdx = getColumnIndex(newCardFields.size);
        const currentColumns: ColumnsData = getColumnsData(lgColumn, smColumn);

        newOrder = currentColumns[targetColumnIdx].length;
        await updateLayout(
          moveCardBetweenColumns(currentColumns, cardId, targetColumnIdx),
        );
      }

      const updatedCards: DashboardCard[] = updateAtId<DashboardCard>(
        cardId,
        R.evolve({
          title: () => newCardFields.title,
          embedLink: () => newCardFields.embedLink,
          size: () => newCardFields.size,
          contentState: () =>
            nameChangeOnly
              ? DashboardCardContentState.READY
              : DashboardCardContentState.LOADING,
          order: () => newOrder,
        }),
      )(allCards);
      setAllCards(updatedCards);
      setLoading(false);
    } catch (error) {
      displayError();
    }
  };

  const loadingEmbedCard =
    creatingCard || deletingCard || updatingCard || loading;

  const contextValue = {
    emptyBoard,
    allCards,
    setAllCards,
    updateCardState,
    updateAllCardsState,
    setShowCustomize,
    lgColumn,
    smColumn,
    updateLayout,
    updatingLayout,
    someCardIsLoading,
    handleAddEmbedCard,
    handleDeleteEmbedCard,
    handleEditEmbedCard,
    loadingEmbedCard,
  };

  const dashboardBody = loadingInitData ? (
    <Progress style={{ height: '80vh' }} />
  ) : emptyBoard ? (
    <DashboardEmptyState />
  ) : (
    <DashboardGrid />
  );

  return (
    <DashboardContext.Provider value={contextValue}>
      {loadingEmbedCard && (
        <Progress
          style={{
            height: '100vh',
            width: '100%',
            position: 'fixed',
            top: 0,
            left: 0,
            opacity: 0.5,
            background: colors.neutral.ash,
            zIndex: 1000,
          }}
        />
      )}
      <div className={classes.portalContainer}>
        <DashboardTitleBar />
        <div className={classes.dashboardHello}>Hello, {firstName}</div>
        {dashboardBody}
      </div>
      <CustomizeDashboardModal
        open={showCustomize}
        onClose={() => setShowCustomize(false)}
      />
    </DashboardContext.Provider>
  );
};

export default DashboardPortal;
